import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import hero from "@/assets/smart-home.webp";
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Highlands Ranch";
const SLUG = "highlands-ranch";
const ZIP = "80129";
const URL = "https://apexav.com/highlands-ranch/smart-home-installation";

const faqs = [
  {
    q: "How much does smart home installation cost in Highlands Ranch?",
    a: "Highlands Ranch smart home projects typically start around $10,000 for a single-system install (smart lighting OR motorized shades OR whole-home audio). Whole-home integrated systems integrating lighting, shading, climate, audio, video, and security generally run $30,000–$150,000+ for Backcountry custom estates. Eastridge, Westridge, and Northridge tract homes most often land in the $20K–$60K range for a complete integrated system. Every project is fixed-bid up front.",
  },
  {
    q: "Which smart home platform is best for a Highlands Ranch home?",
    a: "For most Highlands Ranch tract homes (Eastridge, Westridge, Northridge) we recommend leading wireless lighting platforms paired with industry-standard whole-home automation — it's the most reliable, best-supported combination at this price point. Backcountry custom estates frequently call for fully custom control platforms for ultra-customized UIs. We're certified dealers for multiple platforms and recommend based on your actual home, not what we sell most.",
  },
  {
    q: "Can you retrofit a smart home into my existing Highlands Ranch home without major construction?",
    a: "Yes. Wireless lighting platforms retrofit cleanly into any Highlands Ranch home — every existing switch is replaced with a smart keypad, no wall demolition. Most retrofits wrap in 2–5 days. For wired systems we route low-voltage cabling through basements, attics, and accessible chases without major demo.",
  },
  {
    q: "Will my smart home work if Xfinity goes down?",
    a: "Yes. Lighting, shading, locks, climate, and most automation runs on a local processor inside your home, not in the cloud. Internet only affects remote access from your phone when you're traveling. We install enterprise-grade Highlands Ranch networking (UniFi or Araknis) with optional cellular failover for homes that can't tolerate any downtime.",
  },
  {
    q: "Do you handle Highlands Ranch HOA approvals for exterior work?",
    a: "Yes. The Highlands Ranch Community Association has specific guidelines for exterior cameras, antennas, and solar installs. We handle the documentation and submission as part of any project that touches the exterior — including outdoor TVs, landscape audio, and exterior cameras.",
  },
];

const testimonials = [
  { quote: "Apex installed whole-home automation across our entire Backcountry estate — lighting, shades, audio, theater, security. Six months in, every scene still works perfectly. Best money we've spent on the house.", name: "Andrea T.", location: "Backcountry, Highlands Ranch" },
  { quote: "Wireless lighting retrofit in our Eastridge home — every switch replaced in 4 days, zero wall damage. The 'goodnight' scene alone was worth it.", name: "Chris D.", location: "Eastridge" },
  { quote: "We had Sonos, Nest, Ring, and separate lighting — four different apps. Apex unified everything onto one platform and added a media room theater. One remote, one app, finally.", name: "Priya N.", location: "Westridge" },
];

export const Route = createFileRoute("/highlands-ranch/smart-home-installation")({
  head: () => ({
    meta: [
      { title: "Smart Home Installation Highlands Ranch | Apex AV" },
      { name: "description", content: "Whole-home smart home automation in Highlands Ranch — Backcountry, Eastridge, Westridge. Certified smart home integrator. Free quote." },
      { name: "keywords", content: "smart home installation Highlands Ranch, smart home automation Highlands Ranch, Backcountry smart home, Eastridge home automation, lighting automation Highlands Ranch" },
      { property: "og:title", content: "Smart Home Installation Highlands Ranch — Apex AV" },
      { property: "og:description", content: "Whole-home automation for Highlands Ranch homes." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Smart Home Installation Highlands Ranch — Apex AV" },
      { name: "twitter:description", content: "Whole-home automation for Highlands Ranch homes." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Smart Home Installation in ${NEIGHBORHOOD}`,
              description: `Whole-home smart home automation in ${NEIGHBORHOOD}, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, CO ${ZIP}` },
              serviceType: "Smart Home Automation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "10000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 15000,
              serviceName: "Smart Home Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="smart home installation"
      serviceKey="smart-home"
      parentServiceLabel="smart home automation"
      parentServiceTo="/services/smart-home"
      h1="Smart Home Installation in Highlands Ranch, CO"
      intro="Whole-home automation engineered for Highlands Ranch — Backcountry custom estates, Eastridge and Westridge tract homes, Northridge family residences. Certified smart home integrator. One interface for lighting, shades, climate, audio, video, security, and access."
      hero={hero}
      bullets={[
        "Certified smart home integrator",
        "Wireless RadioRA 3 retrofit (no wall damage)",
        "Whole-home lighting & motorized shades",
        "Voice control via Alexa, Google, Apple Home",
        "Enterprise networking with cellular failover",
        "HOA documentation handled for exterior work",
      ]}
      microNeighborhoods={["Backcountry", "Eastridge", "Westridge", "Northridge", "Southridge", "Highland Heritage"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "The right system for your Highlands Ranch home",
          body: (
            <>
              <p>
                Highlands Ranch smart home projects fall into two patterns. <strong>Tract homes</strong> in Eastridge, Westridge, Northridge, and Southridge typically benefit from a wireless lighting retrofit (lighting and shading), a Sonos or in-wall audio install, and a central control hub that unifies the existing Nest thermostats, Ring cameras, and Apple TV / Roku devices. Total project cost typically lands $20K–$60K and wraps in under 2 weeks.
              </p>
              <p>
                <strong>Backcountry custom estates</strong> get our full whole-home automation treatment: wired control systems (or fully custom platforms for ultra-luxury UIs), whole-home smart lighting, motorized shades on every window, distributed audio in every zone, integrated theater, security, and access — all on one app, one remote, one number to call.
              </p>
            </>
          ),
        },
        {
          heading: "Wireless lighting — the Highlands Ranch retrofit special",
          body: (
            <>
              <p>
                The single fastest, cleanest upgrade for any existing Highlands Ranch home is a wireless lighting retrofit. Every toggle switch in the house is replaced one-for-one with a smart keypad — no wall demolition, no rewiring, no drywall patching. The system runs on a local processor (no cloud dependency), supports remotes that mount on any surface, and integrates with motorized shades, thermostats, and audio without touching wiring.
              </p>
              <p>
                Most Highlands Ranch wireless lighting retrofits — including 4,000+ sqft Eastridge and Westridge homes — wrap in 3–5 days with zero construction. Add motorized shades on the great room and primary bedroom and you have a serious smart home for under $20K.
              </p>
            </>
          ),
        },
        {
          heading: "Whole-home automation for Backcountry estates",
          body: (
            <>
              <p>
                For Backcountry custom estates, we recommend industry-leading whole-home platforms. They handle 50+ device integrations reliably (smart lighting, Sonos audio, Sony or JVC projectors, ecobee thermostats, Ring or DoorBird intercoms, Yale or Schlage locks, Hikvision or Luma cameras), run everything off a local processor, and give you one app, one remote, and customizable on-wall touchscreens.
              </p>
              <p>
                Every install is paired with <strong>enterprise-grade Highlands Ranch networking</strong> (UniFi or Araknis) and a UPS-backed equipment rack so the system stays up through power flickers and Xfinity outages. Cellular failover is available for homes that can't tolerate any downtime.
              </p>
            </>
          ),
        },
        {
          heading: "Add a basement theater to your Highlands Ranch smart home",
          body: (
            <>
              <p>
                Most Highlands Ranch smart home clients add a dedicated basement theater within 12 months. Because the control system is already in place, the theater becomes another zone — one button drops the screen, dims lights, drops the shades, and fires the projector. See our <a href="/highlands-ranch/home-theater-installation" className="text-brand-text underline underline-offset-2">Highlands Ranch home theater installation</a> page for design and pricing.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
